import { Link } from 'react-router-dom';
import Container from '../ui/Container';
import Wordmark from '../ui/Wordmark';
import { footerCapabilityLinks, footerSiteLinks } from '../../utils/navigation';
import { ROUTES } from '../../utils/routes';
import businessInfo, {
  getEmailHref,
  getPhoneDisplay,
  getPhoneHref,
  hasLegalName,
} from '../../utils/businessInfo';

/**
 * Site footer.
 *
 * Carries the contact details a visitor reaches for when the form is not what
 * they want -- email and telephone, in that order, because this audience
 * emails far more often than it calls. The capability links deep link into the
 * Services page sections.
 *
 * The imprint shows the legal entity only once a real name has been supplied;
 * a placeholder in the footer of a live site is worse than no imprint line, so
 * the line is omitted until `legalName` is filled in
 * (see utils/businessInfo.js).
 */
const Footer = () => {
  const emailHref = getEmailHref();
  const phoneHref = getPhoneHref();
  const phoneDisplay = getPhoneDisplay();

  return (
    <footer className="site-footer">
      <Container>
        <div className="site-footer__grid">
          <div className="site-footer__brand">
            <Wordmark size={24} />
            <p className="site-footer__tagline">{businessInfo.tagline}</p>
            <p className="site-footer__message">{businessInfo.primaryMessage}</p>
          </div>

          <nav className="site-footer__column" aria-labelledby="footer-what-we-do">
            <h2 className="site-footer__heading" id="footer-what-we-do">
              What we do
            </h2>
            <ul className="site-footer__list">
              {footerCapabilityLinks.map((link) => (
                <li key={link.to}>
                  <Link className="site-footer__link" to={link.to}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="site-footer__column" aria-labelledby="footer-site">
            <h2 className="site-footer__heading" id="footer-site">
              Site
            </h2>
            <ul className="site-footer__list">
              {footerSiteLinks.map((link) => (
                <li key={link.to}>
                  <Link className="site-footer__link" to={link.to}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-footer__column">
            <h2 className="site-footer__heading" id="footer-contact">
              Talk to us
            </h2>
            <ul className="site-footer__list">
              <li>
                {emailHref ? (
                  <a className="site-footer__link" href={emailHref}>
                    {businessInfo.email}
                  </a>
                ) : (
                  <span className="site-footer__link">{businessInfo.email}</span>
                )}
              </li>
              <li>
                {phoneHref ? (
                  <a className="site-footer__link" href={phoneHref}>
                    {phoneDisplay}
                  </a>
                ) : (
                  <span className="site-footer__link">{phoneDisplay}</span>
                )}
              </li>
            </ul>
            <p className="site-footer__note">{businessInfo.serviceAreaLong}</p>
          </div>
        </div>

        <div className="site-footer__bottom">
          <p className="site-footer__legal">
            &copy; {new Date().getFullYear()} {businessInfo.name}
            {hasLegalName() ? `. ${businessInfo.legalName}.` : '.'}
          </p>
          <Link className="site-footer__link site-footer__back" to={ROUTES.home}>
            Back to top
          </Link>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
